import type { ParsedCartItemGroup } from 'ui/types/cartTypes';
import clsx from 'clsx';
import { Separator } from 'ui/components/Separator/Separator';
import { TRIAL_MEMBERSHIP_SKU } from 'ui/utils/constants/membershipConstants';
import { FAMILY_MEMBERSHIP_SKUS } from 'ui/utils/constants/familyConstants';
import { PlanTypes } from 'ui/utils/constants/maverick';
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { useTranslation } from '@whoop/i18n/lang/client';
import { useShallow } from 'zustand/react/shallow';
import React from 'react';
import { hasProductTypeInCartItemGroups } from '../cart.utils';
import { TwoYearCartUpsell } from '@/components/Cart/CartUpsell/TwoYearCartUpsell';
import CartItem from '@/components/Cart/CartItems/CartItem/CartItem';
import { HsaFsaBadge } from '@/components/HsaFsaBadge/HsaFsaBadge';
import {
  getIsMembershipTypeInCart,
  getIsSomeMembershipInCart,
} from '@/lib/utils/membershipHelper';
import {
  JOIN_FLOW_STEPS,
  ProductType,
  UPGRADE_STEPS,
} from '@/lib/constants/maverick';
import {
  EXPERIMENT_JF_BNPL_EXPOSURE,
  BNPL_EXPOSURE_CART_UPSELL,
} from '@/lib/constants/experiments';
import { useGetCurrentPageMaverick } from '@/hooks/getCurrentPage';
import { TruemedHsaFsaBadge } from '@/components/TruemedHsaFsaBadge/TruemedHsaFsaBadge';
import { useShouldShowTruemedBanner } from '@/stripe/customPaymentMethods/truemed/client/useShouldShowTruemedBanner';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { filterCartItems } from '@/lib/utils/parseCart';

interface CartGroupsProps {
  cartId: string | null;
  cartItemGroups: ParsedCartItemGroup[];
  hideHsaFsaBadge?: boolean;
  hideSeparators?: boolean;
  isCartLocked?: boolean;
}

export default function CartGroups({
  cartId,
  cartItemGroups,
  hideHsaFsaBadge = false,
  hideSeparators = false,
  isCartLocked = false,
}: CartGroupsProps): JSX.Element | null {
  const pathname = usePathname();
  const currentPage = useGetCurrentPageMaverick();
  const { t } = useTranslation('cart');
  const shouldShowTruemedBanner = useShouldShowTruemedBanner();
  const { country, experimentVariants, isBNPLSelected, setIsCartOpen } =
    useSiteStore(
      useShallow((state) => ({
        country: state.country,
        experimentVariants: state.experimentVariants,
        isBNPLSelected: state.isBNPLSelected,
        setIsCartOpen: state.setIsCartOpen,
      })),
    );

  const bnplExperimentVariant = experimentVariants[EXPERIMENT_JF_BNPL_EXPOSURE];
  const isUpgrade = pathname.includes('/upgrade');
  const isJFOrderPage = currentPage === JOIN_FLOW_STEPS.ORDER;
  const itemsToShow = filterCartItems(cartItemGroups);

  if (cartId === null || !cartItemGroups) {
    return null;
  }

  const isTrialInCart =
    getIsSomeMembershipInCart(cartItemGroups, [TRIAL_MEMBERSHIP_SKU]) ||
    // Get by attributes
    getIsMembershipTypeInCart(cartItemGroups, PlanTypes.TRIAL);

  const isAnnualMembershipInCart = getIsMembershipTypeInCart(
    cartItemGroups,
    PlanTypes.ANNUAL,
  );

  const is24MonthMembershipInCart = getIsMembershipTypeInCart(
    cartItemGroups,
    PlanTypes.TWO_YEAR,
  );

  const isFamilyInCart =
    getIsSomeMembershipInCart(cartItemGroups, FAMILY_MEMBERSHIP_SKUS) ||
    // Get by attributes
    getIsMembershipTypeInCart(cartItemGroups, PlanTypes.FAMILY);

  const showHSABadge = () => {
    const shouldShow =
      !isFamilyInCart && !is24MonthMembershipInCart && !hideHsaFsaBadge;

    if (isUpgrade) {
      return (
        shouldShow &&
        hasProductTypeInCartItemGroups(cartItemGroups, ProductType.EXTENSION)
      );
    }
    return shouldShow;
  };

  const description = (cartItemGroup: ParsedCartItemGroup, index: number) => {
    if (index > 0) {
      return (
        <h3 className='text-gray-60 font-regular text-sm'>
          {cartItemGroup.description}
        </h3>
      );
    }

    if (
      !isJFOrderPage &&
      currentPage !== JOIN_FLOW_STEPS.RECEIPT &&
      currentPage !== UPGRADE_STEPS.RECEIPT &&
      !isUpgrade
    ) {
      return (
        <Link href='/' onClick={() => setIsCartOpen(false)}>
          <p className='text-gray-60 font-regular text-sm underline'>
            {t('changeMembership')}
          </p>
        </Link>
      );
    }
  };

  const showTwoYearCartUpsell =
    !isJFOrderPage &&
    !isBNPLSelected &&
    isAnnualMembershipInCart &&
    bnplExperimentVariant === BNPL_EXPOSURE_CART_UPSELL;

  return (
    <div className='flex w-full flex-col gap-4'>
      <div className={clsx('flex w-full flex-col gap-4')}>
        {itemsToShow?.map((cartItemGroup, index) => {
          const hasCartItems = cartItemGroup.cartItems.length > 0;

          if (!hasCartItems) {
            return null;
          }

          return (
            <div
              className={clsx(
                'flex w-full flex-col gap-4 rounded-xl bg-white',
                !hideSeparators && 'p-4',
              )}
              key={cartItemGroup.title}
            >
              <div className='flex flex-col'>
                <h3 className='text-xl font-bold'>{cartItemGroup.title}</h3>
                {description(cartItemGroup, index)}
              </div>
              {!hideSeparators ? <Separator /> : null}
              <div className='flex flex-col gap-2'>
                {cartItemGroup.cartItems.map((item) => (
                  <CartItem
                    cartId={cartId}
                    cartItem={item}
                    image={item.imageUrls[0]}
                    isCartLocked={isCartLocked}
                    isTrialInCart={isTrialInCart}
                    key={item.id}
                  />
                ))}
              </div>

              {index === 0 && showHSABadge() ? (
                <div className='text-gray-b50 flex w-full justify-start'>
                  {shouldShowTruemedBanner && !isTrialInCart ? (
                    <TruemedHsaFsaBadge />
                  ) : (
                    <HsaFsaBadge
                      country={country}
                      isTrialInCart={isTrialInCart}
                      size='small'
                    />
                  )}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
      {showTwoYearCartUpsell ? <TwoYearCartUpsell /> : null}
    </div>
  );
}
